import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { claimRefundBonus, fetchRefundBonusStatus } from 'stores/auth';
import { showToaster } from 'mixin/general';
import useCurrency from 'toolkit/useHooks/useCurrency';

export default function RefundBonusCard() {
  const dispatch = useDispatch();
  const { t } = useTranslation('translations');
  const { sign: currencySign } = useCurrency();
  const user = useSelector((state) => state.auth.user);
  const status = useSelector((state) => state.auth.refundBonusStatus);
  const claiming = useSelector((state) => state.auth.refundBonusClaiming);
  const cooldownUntil = useSelector((state) => state.auth.refundBonusCooldownUntil);
  const statusRetryUntil = useSelector((state) => state.auth.refundBonusStatusRetryUntil);
  const [now, setNow] = useState(Date.now());

  const enabled = Boolean(user?.isRefundBonusEnabled);

  useEffect(() => {
    if (enabled) dispatch(fetchRefundBonusStatus());
  }, [enabled]);

  // Tick once a second only while a cooldown is running
  useEffect(() => {
    if (!cooldownUntil || Date.now() >= cooldownUntil) return undefined;
    setNow(Date.now());
    const id = setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= cooldownUntil) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [cooldownUntil]);

  // No real status yet (only a RATE_LIMITED placeholder): refetch once when the back-off ends
  const waitingForStatus =
    enabled && status?.reason === 'RATE_LIMITED' && Boolean(statusRetryUntil);
  useEffect(() => {
    if (!waitingForStatus) return undefined;
    const id = setTimeout(
      () => dispatch(fetchRefundBonusStatus()),
      Math.max(0, statusRetryUntil - Date.now())
    );
    return () => clearTimeout(id);
  }, [waitingForStatus, statusRetryUntil]);

  if (!enabled || !status || status.reason === 'DISABLED') return null;

  const fmt = (value) =>
    (value ?? 0).toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }) +
    ' ' +
    currencySign;

  const secondsLeft = cooldownUntil ? Math.max(0, Math.ceil((cooldownUntil - now) / 1000)) : 0;
  const inCooldown = secondsLeft > 0;
  const canClaim = status.eligible === true && !claiming && !inCooldown;

  const handleClaim = async () => {
    try {
      const result = await dispatch(claimRefundBonus()).unwrap();
      if (result?.granted) {
        showToaster(t('refundBonus.success', { amount: fmt(result.grantedAmount) }), 'success');
      } else {
        showToaster(
          t(`refundBonus.reason.${result?.reason}`, {
            count: result?.openBetCount ?? status.openBetCount,
            balance: fmt(result?.balance ?? status.balance),
            minStake: fmt(result?.minStake ?? status.minStake),
            deposit: fmt(result?.depositAmount ?? status.depositAmount),
            withdraw: fmt(result?.withdrawAmount ?? status.withdrawAmount),
            defaultValue: t('refundBonus.error')
          }),
          'info'
        );
      }
      if (result?.reason !== 'RATE_LIMITED') dispatch(fetchRefundBonusStatus());
    } catch (e) {
      showToaster(t('refundBonus.error'), 'error');
    }
  };

  const reasonText =
    status.eligible || !status.reason || status.reason === 'ELIGIBLE'
      ? null
      : t(`refundBonus.reason.${status.reason}`, {
          count: status.openBetCount,
          balance: fmt(status.balance),
          minStake: fmt(status.minStake),
          deposit: fmt(status.depositAmount),
          withdraw: fmt(status.withdrawAmount),
          defaultValue: ''
        });

  let label =
    status.eligible === true && status.estimatedAmount > 0
      ? t('refundBonus.claim', { amount: fmt(status.estimatedAmount) })
      : t('refundBonus.claimNoAmount');
  if (claiming) label = t('refundBonus.claiming');
  else if (inCooldown) label = t('refundBonus.cooldown', { seconds: secondsLeft });

  return (
    <Card className="mb-3">
      <Card.Body>
        {reasonText && <p className="small text-muted mb-2">{reasonText}</p>}
        <Button
          variant="primary"
          className="w-100"
          disabled={!canClaim}
          onClick={canClaim ? handleClaim : undefined}>
          {label}
        </Button>
      </Card.Body>
    </Card>
  );
}
